import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AuthService, BaseComponent, DepartureArrivalTime } from '@ridango/core';
import { AccountTicketsNgrxFacade, Ticket, TicketStatus, FavoritesFacade, FavoriteTicket } from '@ridango/components';
import Env from '@ridango/core/env';
import { DeviceIdService, PullToRefreshService } from '@ridango/core/lib/services';
import { TranslateService } from '@ngx-translate/core';
import { FareContractStatus } from '@ridango/components/lib/account-tickets-ngrx/models/ticket-status.model';
import { Observable } from 'rxjs';
import { Temporal } from '@js-temporal/polyfill';
import { TicketHistoryFacade } from '@ridango/components/lib/ticket-history';
import { TicketHistoryFilterState } from '@ridango/components/lib/ticket-history/ticket-history.model';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { ProductCode } from '@ridango/components/lib/purchase/purchase.model';

export enum Direction {
    TO = 'To',
    FROM = 'From'
}

@Component({
    selector: 'rwidget-history-ticket-card-container',
    templateUrl: './history-ticket-card-container.component.html',
    styleUrls: ['history-ticket-card-container.component.scss']
})
export class HistoryTicketCardContainerComponent extends BaseComponent implements OnInit, OnDestroy {
    TEN_TRIP_TICKET_UNITS = 10;

    public tickets$: Observable<{ key: string; value: Ticket[] }[]> = this.accountTicketsFacade.accountTickets$.pipe(
        map((tickets) =>
            tickets.filter((ticket) => {
                if (this.env.IS_NEW_AFC) {
                    return this.isFilterTicketByDateAndStatues(ticket);
                }

                if (
                    [TicketStatus.Returned, TicketStatus.Refunded, TicketStatus.Changed].includes(
                        ticket.status as TicketStatus
                    )
                ) {
                    if (ticket.categoryCode === ProductCode.MULTI_JOURNEY) {
                        return ticket.starting || ticket.ending || ticket.bookletEnding;
                    }
                    return ticket.bookletEnding || ticket.timestamp > this.minToDate;
                }

                return ticket.starting || ticket.bookletEnding;
            })
        ),
        map((tickets) =>
            tickets.sort((a, b) => {
                const getTicketDate = (ticket: Ticket): Date => {
                    if (ticket.ending) {
                        return new Date(ticket.ending);
                    }

                    if (ticket.endDate && this.env.IS_NEW_AFC) {
                        return new Date(ticket.endDate);
                    }

                    if (
                        [TicketStatus.Returned, TicketStatus.Refunded, TicketStatus.Changed].includes(
                            ticket.status as TicketStatus
                        ) &&
                        ticket.initialUnits !== this.TEN_TRIP_TICKET_UNITS
                    ) {
                        return new Date(ticket.ending || ticket.timestamp);
                    }

                    if (ticket.bookletEnding) {
                        if (
                            [TicketStatus.Returned, TicketStatus.Refunded, TicketStatus.Changed].includes(
                                ticket.status as TicketStatus
                            )
                        ) {
                            return new Date(ticket.timestamp);
                        }
                        return new Date(ticket.bookletEnding);
                    }

                    return new Date();
                };

                const dateA = getTicketDate(a);
                const dateB = getTicketDate(b);
                return (
                    dateB.getFullYear() - dateA.getFullYear() ||
                    dateB.getMonth() - dateA.getMonth() ||
                    dateB.getTime() - dateA.getTime()
                );
            })
        ),
        map((tickets) => {
            const groupedByMonth = this.groupedByMonth(tickets);
            return Array.from(groupedByMonth, ([key, value]) => ({ key, value }));
        })
    );

    public ticketsLoaded$ = this.accountTicketsFacade.loaded$;

    public fromDateValue: DepartureArrivalTime;
    public toDateValue: DepartureArrivalTime;
    public minFromDate: Date | string;
    public minToDate: Date | string;
    public maxFromDate: Date | string;
    public maxToDate: Date | string;
    private readonly EXPIRED = 'Expired';
    public readonly Direction = Direction;
    public favoriteTickets: FavoriteTicket[] = [];

    constructor(
        @Inject('ENV') public env: Env,
        public datePipe: DatePipe,
        private accountTicketsFacade: AccountTicketsNgrxFacade,
        private ticketHistoryFacade$: TicketHistoryFacade,
        private authService: AuthService,
        private pullToRefresh: PullToRefreshService,
        private deviceIdService: DeviceIdService,
        public translate: TranslateService,
        private analyticsService: AnalyticsService,
        private favoritesFacade: FavoritesFacade
    ) {
        super();
    }

    ngOnInit() {
        this.ticketHistoryFacade$
            .getTicketHistorySearchFilterState()
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter((filter) => !!filter)
            )
            .subscribe((filter: TicketHistoryFilterState) => {
                this.fromDateValue = { datetime: filter.fromDate };
                this.toDateValue = { datetime: filter.toDate };

                if (!this.maxFromDate && !this.maxToDate) {
                    this.maxFromDate = filter.todayDate;
                    this.maxToDate = filter.todayDate;
                }

                // No point in letting 'toDate' go earlier than is 'fromDate'
                this.minToDate = filter.fromDate;
            });

        this.initializeHistoryDate();

        this.pullToRefresh.methodCalled$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => this.initializeHistoryDate());

        if (this.env.SHOW_FAVORITE_TICKETS && this.authService.getLoginStatus()) {
            this.favoritesFacade.getFavoriteTickets();
            this.favoritesFacade.favoriteTickets$.pipe(takeUntil(this.componentDestroyed$)).subscribe((tickets) => {
                this.favoriteTickets = tickets;
            });
        }
    }

    public fetchAccountTicketsHistory(): void {
        const formattedFromDate = this.datePipe.transform(this.fromDateValue.datetime, 'yyyyMMdd');
        const formattedToDate = this.datePipe.transform(this.toDateValue.datetime, 'yyyyMMdd');
        const includeReturnedTickets = true;

        this.accountTicketsFacade.fetchAccountTicketsHistory(
            formattedFromDate,
            formattedToDate,
            includeReturnedTickets
        );
    }

    public changeFromDate() {
        this.trackDateChanges(Direction.FROM);
        this.ticketHistoryFacade$.updateTicketHistorySearchFilter({
            fromDate: Temporal.PlainDate.from(this.fromDateValue.datetime).toString()
        });
        this.fetchAccountTicketsHistory();
    }

    public changeToDate() {
        this.trackDateChanges(Direction.TO);
        this.ticketHistoryFacade$.updateTicketHistorySearchFilter({
            toDate: Temporal.PlainDate.from(this.toDateValue.datetime).toString()
        });
        this.fetchAccountTicketsHistory();
    }

    trackDateChanges(direction: Direction): void {
        this.analyticsService.eventTrack(TrackedEventAction.FILTER_APPLIED, {
            category: TrackedEventCategory.TICKETS,
            label: `${this.EXPIRED} ${direction}`
        });
    }

    public initializeHistoryDate() {
        this.deviceIdService
            .isDeviceUserExist()
            .pipe(
                map((isExist) => {
                    if (isExist && !this.deviceIdService.getAccessDeviceToken()) {
                        return this.deviceIdService.refreshAccessDeviceToken().pipe(map(() => isExist));
                    }

                    return isExist;
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((deviceUserExist) => {
                const isAuthenticated = this.authService.getLoginStatus();
                if (deviceUserExist || isAuthenticated) {
                    this.fetchAccountTicketsHistory();
                }
            });
    }

    getMonthText(ticket: Ticket): string {
        let date: string | number | Date;

        switch (true) {
            case !!(ticket.ending || ticket.endDate):
                date = ticket.ending;
                break;
            case !!(ticket.starting || ticket.startDate):
                date = ticket.starting;
                break;
            case ticket.status === TicketStatus.Returned || ticket.status === TicketStatus.Refunded:
                date = ticket.timestamp;
                break;
            default:
                date = ticket.bookletEnding;
        }

        // Flex and some old tickets don't have any datetime property in response for NEW AFC so the date will be undefined
        if (!date) {
            date = new Date();
        }

        const dateMonthYear = new Intl.DateTimeFormat(this.translate.currentLang, {
            month: 'long',
            year: 'numeric'
        }).format(new Date(date));

        return dateMonthYear;
    }

    private groupedByMonth = (tickets: Ticket[]): Map<string, Ticket[]> => {
        return tickets.reduce((map, ticket) => {
            const monthYear = this.getMonthText(ticket);
            if (!map.has(monthYear)) {
                map.set(monthYear, []);
            }
            map.get(monthYear).push(ticket);
            return map;
        }, new Map<string, Ticket[]>());
    };

    private isFilterTicketByDateAndStatues(ticket): boolean {
        return (
            ticket.startDate ||
            ticket.endDate ||
            ticket.status === FareContractStatus.CANCELLED ||
            ticket.status === FareContractStatus.REFUNDED
        );
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    getFormattedDate(date: string | Date): string {
        if (!date) return '';
        return this.datePipe.transform(date, 'yyyyMMdd') || '';
    }

    onDateFilterClick(direction: Direction) {
        this.analyticsService.eventTrack(TrackedEventAction.FILTER_OPENED, {
            category: TrackedEventCategory.TICKETS,
            name: `${this.EXPIRED} ${direction}`
        });
    }
}
